import { InvoiceMutationDto, InvoiceStatus } from '../../../config/finance/invoiceConfig'
import { useFormikContext } from 'formik'
import { AxiosError, AxiosRequestConfig } from 'axios'
import { Text } from '@fluentui/react'
import ErrorPopover from '../../../components/Toast/ErrorPopover'
import { normalizeAxiosError } from '../../../reducers/errorReducer'
import * as React from 'react'
import useAxios from '../../useAxios'
import useAppToast from '../../useAppToast'
import { useTranslation } from 'react-i18next'

export type PersistInvoiceDto = {
    invoiceId: string
    invoiceNumber: string
    children?: PersistInvoiceDto[]
}

export type PersistInvoice = (invoiceStatus: InvoiceStatus, callback: (data: PersistInvoiceDto) => void) => Promise<void>

export default function usePersistInvoice(): PersistInvoice {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const { values, submitForm } = useFormikContext<InvoiceMutationDto>()
    const toast = useAppToast()

    return (invoiceStatus, callback): Promise<void> => {
        if (values.children?.length > 0) {
            for (let i = 0; i < values.children.length; i++) {
                const child = values.children[i]
                values.children[i] = {
                    ...values,
                    ...child,
                    //cleanup subinvoice
                    children: [],
                    lineItemGroups: [],
                }
            }
        }

        const submit = submitForm() as unknown as Promise<Pick<AxiosRequestConfig, 'url' | 'method'> | undefined>
        return submit.then(params => {
            if (params === undefined) {
                // The form didn't validate.
                return Promise.resolve()
            }

            return toast
                .promise(
                    axios
                        .request({
                            ...params,
                            data: { ...values, status: invoiceStatus },
                        })
                        .then(result => result.data),
                    {
                        pending: translate('invoice.toast.queued', { number: values.number }),
                        success: () => translate('invoice.toast.finished', { number: values.number }),
                        error: (reason: AxiosError) => (
                            <>
                                <Text>{translate('invoice.toast.failed', { number: values.number })}</Text>
                                <ErrorPopover error={normalizeAxiosError(reason)} />
                            </>
                        ),
                    },
                )
                .then((result: PersistInvoiceDto) => callback(result))
        })
    }
}
